import dayjs from 'dayjs';

import type {
  GrandPrixCard,
  GrandPrixCardMap,
  GrandPrixCardPatchPayload,
  GrandPrixCardState,
  GrandPrixCardsResponse,
  GrandPrixSlot,
  GrandPrixTheme,
  MatchLite,
  MatchesByStage,
  RawMatchesByStage,
  TeamInfo,
} from './types';

export const GRAND_PRIZE_SLOTS: GrandPrixSlot[] = ['sf1', 'sf2', 'tp', 'final'];

export const GRAND_PRIZE_TABS: Array<{
  slot: GrandPrixSlot;
  labelKey: string;
}> = [
  { slot: 'sf1', labelKey: 'tabs.sf1' },
  { slot: 'sf2', labelKey: 'tabs.sf2' },
  { slot: 'tp', labelKey: 'tabs.tp' },
  { slot: 'final', labelKey: 'tabs.final' },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value?: unknown) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

function toOptionalNumber(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;

  return toNumber(value);
}

export function formatDateTime(value?: string | null) {
  if (!value) return '--';

  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

export function getTeamName(team?: TeamInfo | null, locale: string = 'en') {
  if (!team) return '';
  if (locale === 'zh' || locale === 'cn') {
    return team.nameZh || team.code || '';
  }

  return team.nameEn || team.code || '';
}

export function getMatchTitle(match?: MatchLite | null) {
  if (!match) return '--';

  const home = match.homeTeam ? getTeamName(match.homeTeam) : match.homePlaceholder || '--';
  const away = match.awayTeam ? getTeamName(match.awayTeam) : match.awayPlaceholder || '--';

  return `${home} vs ${away}`;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function getMatchOptionLabel(match: MatchLite, t?: Translate) {
  const stage = match.stage || match.stageLabel || '--';

  let midText = '--';
  if (stage === 'group') {
    midText = t
      ? t('module.worldCup.focusMatch.stage.group', { group: match.groupName || '--' })
      : `Group ${match.groupName || '--'}`;
  } else {
    midText = t
      ? t(`module.worldCup.focusMatch.stage.${stage}`)
      : stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' ');
  }

  return `${getMatchTitle(match)} · ${midText} · ${formatDateTime(match.kickoffAt)}`;
}

export function getMatchStatusText(match?: MatchLite | null) {
  if (!match) return '--';

  if (match.status === 'live') {
    return `进行中 ${match.liveMinute ?? '--'}′`;
  }

  if (match.status === 'finished') {
    return '已结束';
  }

  return formatDateTime(match.kickoffAt);
}

export function normalizeMatchesByStage(raw?: RawMatchesByStage | null): MatchesByStage {
  return {
    group: ensureArray<MatchLite>(raw?.group),
    roundOf32: ensureArray<MatchLite>(raw?.roundOf32 ?? raw?.round_of_32),
    roundOf16: ensureArray<MatchLite>(raw?.roundOf16 ?? raw?.round_of_16),
    quarterFinal: ensureArray<MatchLite>(raw?.quarterFinal ?? raw?.quarter_final),
    semiFinal: ensureArray<MatchLite>(raw?.semiFinal ?? raw?.semi_final),
    thirdPlace: ensureArray<MatchLite>(raw?.thirdPlace ?? raw?.third_place),
    final: ensureArray<MatchLite>(raw?.final),
  };
}

export function getCandidateMatchesBySlot(
  slot: GrandPrixSlot,
  matchesByStage?: MatchesByStage | null
) {
  if (!matchesByStage) return [];

  if (slot === 'sf1' || slot === 'sf2') {
    return ensureArray<MatchLite>(matchesByStage.semiFinal);
  }

  if (slot === 'tp') {
    return ensureArray<MatchLite>(matchesByStage.thirdPlace);
  }

  return ensureArray<MatchLite>(matchesByStage.final);
}

const SLOT_THEME: Record<GrandPrixSlot, GrandPrixTheme> = {
  sf1: 'brand',
  sf2: 'brand',
  tp: 'secondary',
  final: 'gold',
};

export function getDefaultCard(slot: GrandPrixSlot): GrandPrixCard {
  return {
    slot,
    slotName: '',
    linkedMatch: null,
    matchId: null,
    theme: SLOT_THEME[slot],
    cardState: 'awaiting',
    titleZh: '',
    titleEn: '',
    perUserStakeCap: 0,
    perMatchStakeCap: 0,
    openHoursBeforeKickoff: 0,
    cutoffMinutesBeforeKickoff: 0,
    poolUsdc: '0',
    recoveryRatio: 0,
    visibleOnFront: true,
    isLocked: false,
    lockedReason: null,
    orderCount: 0,
    totalVouchers: undefined,
    userVouchers: undefined,
    mirrorVouchers: undefined,
  };
}

export function getDefaultCardMap(): GrandPrixCardMap {
  return {
    sf1: getDefaultCard('sf1'),
    sf2: getDefaultCard('sf2'),
    tp: getDefaultCard('tp'),
    final: getDefaultCard('final'),
  };
}

function normalizeSlot(value: unknown): GrandPrixSlot | null {
  if (!value) return null;

  const key = String(value).toLowerCase();

  if (key === 'sf1' || key === 'semi_final_1' || key === 'semifinal1') return 'sf1';
  if (key === 'sf2' || key === 'semi_final_2' || key === 'semifinal2') return 'sf2';
  if (key === 'tp' || key === 'third_place' || key === 'thirdplace') return 'tp';
  if (key === 'final') return 'final';

  return null;
}

function normalizeTheme(value: unknown, fallback: GrandPrixTheme): GrandPrixTheme {
  if (value === 'brand' || value === 'gold' || value === 'secondary') return value;

  if (value === 'standard') return 'brand';

  return fallback;
}

export function normalizeCard(raw: unknown, slot: GrandPrixSlot): GrandPrixCard {
  const fallback = getDefaultCard(slot);

  if (!isRecord(raw)) {
    return fallback;
  }

  const linkedMatch = isRecord(raw.linkedMatch)
    ? (raw.linkedMatch as MatchLite)
    : isRecord(raw.linked_match)
      ? (raw.linked_match as MatchLite)
      : null;

  const rawMatchId =
    raw.linkedMatchId ??
    raw.linked_match_id ??
    raw.matchId ??
    raw.match_id ??
    linkedMatch?.id ??
    null;

  return {
    ...fallback,
    slot,
    slotName: String(raw.slotName ?? raw.slot_name ?? fallback.slotName),
    linkedMatch,
    matchId: rawMatchId === null || rawMatchId === undefined ? null : String(rawMatchId),
    theme: normalizeTheme(raw.theme, fallback.theme),
    cardState: (raw.cardState ?? raw.card_state ?? fallback.cardState) as GrandPrixCardState,
    titleZh: String(raw.titleZh ?? raw.title_zh ?? fallback.titleZh),
    titleEn: String(raw.titleEn ?? raw.title_en ?? fallback.titleEn),
    perUserStakeCap: toNumber(
      raw.perUserStakeCap ?? raw.per_user_stake_cap ?? fallback.perUserStakeCap
    ),
    perMatchStakeCap: toNumber(
      raw.perMatchStakeCap ?? raw.per_match_stake_cap ?? fallback.perMatchStakeCap
    ),
    openHoursBeforeKickoff: toNumber(
      raw.openHoursBeforeKickoff ?? raw.open_hours_before_kickoff ?? fallback.openHoursBeforeKickoff
    ),
    cutoffMinutesBeforeKickoff: toNumber(
      raw.cutoffMinutesBeforeKickoff ??
        raw.cutoff_minutes_before_kickoff ??
        fallback.cutoffMinutesBeforeKickoff
    ),
    poolUsdc: String(raw.poolUsdc ?? raw.pool_usdc ?? fallback.poolUsdc),
    recoveryRatio: toNumber(raw.recoveryRatio ?? raw.recovery_ratio ?? fallback.recoveryRatio),
    visibleOnFront:
      typeof raw.visibleOnFront === 'boolean'
        ? raw.visibleOnFront
        : typeof raw.visible_on_front === 'boolean'
          ? raw.visible_on_front
          : fallback.visibleOnFront,
    isLocked:
      typeof raw.isLocked === 'boolean'
        ? raw.isLocked
        : typeof raw.is_locked === 'boolean'
          ? raw.is_locked
          : fallback.isLocked,
    lockedReason:
      raw.lockedReason === null || raw.locked_reason === null
        ? null
        : raw.lockedReason
          ? String(raw.lockedReason)
          : raw.locked_reason
            ? String(raw.locked_reason)
            : fallback.lockedReason,
    orderCount: toNumber(raw.orderCount ?? raw.order_count ?? fallback.orderCount),
    configVersion: toOptionalNumber(raw.configVersion ?? raw.config_version),
    updatedBy: raw.updatedBy
      ? String(raw.updatedBy)
      : raw.updated_by
        ? String(raw.updated_by)
        : undefined,
    updatedAt: raw.updatedAt
      ? String(raw.updatedAt)
      : raw.updated_at
        ? String(raw.updated_at)
        : undefined,
    totalVouchers: toOptionalNumber(raw.totalVouchers ?? raw.total_vouchers),
    userVouchers: toOptionalNumber(raw.userVouchers ?? raw.user_vouchers),
    mirrorVouchers: toOptionalNumber(raw.mirrorVouchers ?? raw.mirror_vouchers),
    whitelistVouchers: toOptionalNumber(raw.whitelistVouchers ?? raw.whitelist_vouchers),
  };
}

function getCardsSource(raw: GrandPrixCardsResponse | unknown) {
  if (!raw) return null;

  if (Array.isArray(raw)) return raw;

  if (!isRecord(raw)) return null;

  if (Array.isArray(raw.cards) || isRecord(raw.cards)) return raw.cards;
  if (Array.isArray(raw.items)) return raw.items;
  if (Array.isArray(raw.list)) return raw.list;

  if (Array.isArray(raw.data) || isRecord(raw.data)) {
    const data = raw.data;

    if (isRecord(data) && (Array.isArray(data.cards) || isRecord(data.cards))) {
      return data.cards;
    }

    return data;
  }

  return raw;
}

function getRawCardBySlot(source: unknown, slot: GrandPrixSlot) {
  if (Array.isArray(source)) {
    return source.find((item) => {
      if (!isRecord(item)) return false;

      return normalizeSlot(item.slot) === slot;
    });
  }

  if (!isRecord(source)) return null;

  if (slot in source) {
    return source[slot];
  }

  return null;
}

export function normalizeCards(raw: GrandPrixCardsResponse | unknown): GrandPrixCardMap {
  const source = getCardsSource(raw);
  const defaults = getDefaultCardMap();

  return GRAND_PRIZE_SLOTS.reduce((acc, slot) => {
    acc[slot] = normalizeCard(getRawCardBySlot(source, slot), slot);

    return acc;
  }, defaults);
}

export function buildPatchPayload(
  draft: GrandPrixCard,
  original: GrandPrixCard
): GrandPrixCardPatchPayload {
  const payload: GrandPrixCardPatchPayload = {};
  const locked = draft.isLocked || original.isLocked;

  if (!locked && String(draft.matchId ?? '') !== String(original.matchId ?? '')) {
    if (draft.matchId) {
      payload.linkedMatchId = String(draft.matchId);
    } else {
      payload.clearLinkedMatch = true;
    }
  }

  if (draft.theme !== original.theme) {
    payload.theme = draft.theme;
  }

  if (draft.cardState !== original.cardState) {
    payload.cardState = draft.cardState;
  }

  if (draft.titleZh !== original.titleZh) {
    payload.titleZh = draft.titleZh;
  }

  if (draft.titleEn !== original.titleEn) {
    payload.titleEn = draft.titleEn;
  }

  if (Number(draft.perUserStakeCap) !== Number(original.perUserStakeCap)) {
    payload.perUserStakeCap = Number(draft.perUserStakeCap);
  }

  if (Number(draft.perMatchStakeCap) !== Number(original.perMatchStakeCap)) {
    payload.perMatchStakeCap = Number(draft.perMatchStakeCap);
  }

  if (Number(draft.openHoursBeforeKickoff) !== Number(original.openHoursBeforeKickoff)) {
    payload.openHoursBeforeKickoff = Number(draft.openHoursBeforeKickoff);
  }

  if (Number(draft.cutoffMinutesBeforeKickoff) !== Number(original.cutoffMinutesBeforeKickoff)) {
    payload.cutoffMinutesBeforeKickoff = Number(draft.cutoffMinutesBeforeKickoff);
  }

  if (!locked && String(draft.poolUsdc) !== String(original.poolUsdc)) {
    payload.poolUsdc = String(draft.poolUsdc || '0');
  }

  if (!locked && Number(draft.recoveryRatio ?? 0) !== Number(original.recoveryRatio ?? 0)) {
    payload.recoveryRatio = Number(draft.recoveryRatio ?? 0);
  }

  if (draft.visibleOnFront !== original.visibleOnFront) {
    payload.visibleOnFront = draft.visibleOnFront;
  }

  return payload;
}

export function isCardEqual(draft?: GrandPrixCard | null, original?: GrandPrixCard | null) {
  if (!draft || !original) return draft === original;

  return Object.keys(buildPatchPayload(draft, original)).length === 0;
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;

    if (typeof error.error === 'string') return error.error;

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
